import $ from 'jquery'

import ace from 'ace-builds'

import 'ace-builds/webpack-resolver'
import 'ace-builds/src-noconflict/ace'
import 'ace-builds/src-noconflict/mode-yaml'
import 'ace-builds/src-noconflict/ext-language_tools'
import 'ace-builds/src-noconflict/ext-searchbox'

export default class TemplateTestEditor {
  static initialize($dom) {
    return $dom
      .find("[data-behaviour='template-test-editor']")
      .each(function () {
        return new TemplateTestEditor(this)
      })
  }

  static SampleCode() {
    return `
sample_test:
  context:
    period: 2021-12-31
  data:
    periods:
      2021-12-31:
  expectation:
    reconciled: true
    results:
      some_result: 123`.trim()
  }

  constructor(element) {
    this.test = this.test.bind(this)
    this.runSingleTest = this.runSingleTest.bind(this)
    this.jumpToNextIssue = this.jumpToNextIssue.bind(this)
    this.insertSampleCode = this.insertSampleCode.bind(this)
    this.showDiff = this.showDiff.bind(this)
    this.hideDiff = this.hideDiff.bind(this)
    this.getCurrentLine = this.getCurrentLine.bind(this)
    this.showSuccessMessage = this.showSuccessMessage.bind(this)

    this.element = $(element)
    this.defineElements()

    this.initializeListeners()
    this.toggleDiff()
  }

  defineElements() {
    this.running = false
    this.busChannel = null
    this.busLastId = null

    this.input = this.element.find('textarea')
    this.originalValue = this.input.val()
    this.input.hide()

    this.testIcon = this.element.find("[data-action='test'] i")
    this.singleTestIcon = this.element.find("[data-action='runSingleTest'] i")
    this.testUrl = this.input.data('test-url')
    this.diffUrl = this.input.data('diff-url')
    this.diffContainer = this.element.find(
      "[data-behaviour='sf-ace-editor-diff']"
    )

    this.editorElement = $(
      "<div class='sf-ace-editor-container sf-layer-border'></div>"
    ).appendTo(this.element)

    this.editor = ace.edit(this.editorElement[0], {
      mode: 'ace/mode/yaml',
      minLines: 30,
      maxLines: 30,
      tabSize: 2,
      // This shows tabs, not spaces; the option is implemented wrong:
      // https://github.com/ajaxorg/ace/issues/4220#issuecomment-673444238
      showInvisibles: 'space',
      showPrintMargin: false,
      value: this.originalValue,
    })
  }

  initializeListeners() {
    this.editor.session.on('change', () => {
      this.input.val(this.editor.getValue())
      return this.input.trigger('change')
    })

    this.element.on(
      'click',
      "[data-behaviour='template-test-action']",
      (event) => {
        return this[$(event.currentTarget).data('action')]()
      }
    )

    this.element.on('click', "[data-behaviour='sf-ace-editor-diff']", () => {
      return this.hideDiff()
    })

    this.editor.commands.addCommand({
      name: 'test',
      bindKey: {
        win: 'Ctrl-Enter',
        mac: 'Command-Enter',
        sender: '@editor|cli',
      },
      exec: (_env, _args, _request) => {
        return this.test()
      },
    })
  }

  // This functionality is supported out of the box by Ace (
  // https://github.com/ajaxorg/ace/blob/v1.4.9/lib/ace/commands/default_commands.js#L57),
  // but it adds some weird, non-customizable styling
  jumpToNextIssue() {
    const annotations = this.editor
      .getSession()
      .getAnnotations()
      .filter((a) => a.type === 'error')
    if (annotations.length === 0) {
      return
    }

    const annotatedLines = annotations.map((a) => a.row)
    const currentLine = this.editor.getCursorPosition().row
    const nextLine =
      (annotatedLines.find((l) => l > currentLine) || annotatedLines[0]) + 1

    this.editor.gotoLine(nextLine)
    this.editor.focus()
  }

  insertSampleCode() {
    const { snippetManager } = ace.require('ace/snippets')
    snippetManager.insertSnippet(this.editor, TemplateTestEditor.SampleCode())
    return this.editor.focus()
  }

  showDiff() {
    if (!this.diffContainer) {
      return
    }

    const oldText = this.originalValue
    const newText = this.editor.getValue()

    if (oldText === newText) {
      this.diffContainer
        .html('<div class="no-diff">No changes to show.</p>')
        .show()
      return (this.diffVisible = true)
    }

    return $.post(
      this.diffUrl,
      { old_text: oldText, new_text: newText },
      (data) => {
        this.diffContainer.html(data.html).show()
        return (this.diffVisible = true)
      }
    )
  }

  hideDiff() {
    if (!this.diffContainer) {
      return
    }
    this.diffContainer.html('').hide()
    this.diffVisible = false
    return this.editor.focus()
  }

  toggleDiff() {
    if (this.diffVisible) {
      this.showDiff()
    } else {
      this.hideDiff()
    }
  }

  animateTestIcons(singleTest = false) {
    if (singleTest) {
      this.singleTestIcon
        .removeClass('fa-play')
        .addClass('fa')
        .addClass('fa-spinner')
        .addClass('fa-spin')
    } else {
      this.testIcon
        .removeClass('fa-fast-forward')
        .addClass('fa-spinner')
        .addClass('fa-spin')
    }
  }

  resetTestIcons() {
    this.testIcon
      .addClass('fa-fast-forward')
      .removeClass('fa-spinner')
      .removeClass('fa-spin')
    this.singleTestIcon
      .addClass('fa-play')
      .removeClass('fa-spinner')
      .removeClass('fa-spin')
  }

  setTestBadge(value, singleTest = false) {
    const icon = singleTest ? this.singleTestIcon : this.testIcon
    $('<div class="sf-icon-badge">' + value + '</div>').appendTo(icon)
  }

  clearTestBadge() {
    this.testIcon.find('.sf-icon-badge').remove()
    this.singleTestIcon.find('.sf-icon-badge').remove()
  }

  test(testLine = null) {
    if (!this.testUrl) return
    if (this.running) return

    this.running = true
    this.editor.getSession().clearAnnotations()
    this.clearTestBadge()
    const singleTest = testLine !== null
    this.animateTestIcons(singleTest)

    Silverfin.Spinner.start()

    const form = this.element.closest('form')
    const data = $(form).serializeArray()
    if (testLine) data.push({ name: 'test_line', value: testLine.number })

    const handleResponse = (data) => {
      window.Silverfin.Polling.unsubscribe(this.busChannel, () => {})
      this.running = false
      this.resetTestIcons()
      this.annotate(JSON.parse(data), singleTest)
    }

    $.post(this.testUrl, $.param(data), (response) => {
      this.busChannel = response['channel']
      this.busLastId = response['last_id']

      window.Silverfin.Polling.subscribe(
        this.busChannel,
        handleResponse,
        this.busLastId
      )
    })
  }

  getCurrentLine() {
    const lineNumber = this.editor.selection.cursor.row
    let lineContent = this.editor.session.getLine(lineNumber)
    lineContent = lineContent && lineContent.replace(':', '').trim()

    return {
      number: lineNumber,
      content: lineContent,
    }
  }

  runSingleTest() {
    const currentLine = this.getCurrentLine()
    this.test(currentLine)
  }

  showSuccessMessage(testLine) {
    let successMessage = 'All tests have passed'
    let lineNumber = 0

    if (testLine) {
      successMessage = `Test ${testLine.content} has passed`
      lineNumber = testLine.number
    }

    this.editor
      .getSession()
      .setAnnotations([{ row: lineNumber, text: successMessage, type: 'info' }])
  }

  annotate(annotations, singleTest) {
    const errors = annotations.filter((a) => a['type'] === 'error')

    if (errors.length > 0) {
      const error = errors[0]
      const line = (error['row'] || 0) + 1

      Silverfin.Spinner.failed()
      this.editor.focus()
      this.editor.gotoLine(line)
      this.editor.getSession().setAnnotations(errors)
      this.setTestBadge(errors.length, singleTest)
    } else {
      Silverfin.Spinner.success()
      this.showSuccessMessage() // TODO get success annotations from backend
    }
  }
}

// TODO move out the initialization?
onDomLoad(($dom) => {
  TemplateTestEditor.initialize($dom)
})
